import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { CT } from "@/lib/connectionsTheme";

const Stat = ({ label, value, testId }) => (
  <div className={`${CT.panel} p-5`} data-testid={testId}>
    <p className={CT.label}>{label}</p>
    <p className="mt-2 text-2xl font-extrabold text-slate-900 dark:text-white">{value}</p>
  </div>
);

export const IntakeAnalyticsTab = () => {
  const { API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/org/intake/analytics`, { withCredentials: true })
      .then((res) => setData(res.data))
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API]);

  if (error) {
    return <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400" data-testid="intake-analytics-error">{error}</p>;
  }
  if (!data) {
    return <p className="text-sm text-slate-500" data-testid="intake-analytics-loading">Loading…</p>;
  }

  const conversion = data.conversion_rate === null || data.conversion_rate === undefined ? "—" : `${data.conversion_rate}%`;
  const sources = Object.entries(data.submissions_by_source || {});

  return (
    <div className="space-y-4" data-testid="intake-analytics-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total Scans" value={data.total_scans} testId="intake-analytics-scans" />
        <Stat label="Verified Submissions" value={data.total_submissions} testId="intake-analytics-submissions" />
        <Stat label="Active QR Codes" value={data.active_qr_codes} testId="intake-analytics-active-qr" />
        <Stat label="Conversion" value={conversion} testId="intake-analytics-conversion" />
      </div>

      <div className={`${CT.panel} p-5`} data-testid="intake-analytics-source-breakdown">
        <p className={CT.cardTitle}>Submissions by Source</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {sources.length === 0 ? (
            <p className="text-sm text-slate-500">No submissions yet.</p>
          ) : (
            sources.map(([source, count]) => (
              <span key={source} className={`${CT.badge} ${CT.badgeBlue}`} data-testid={`intake-analytics-source-${source}`}>{source}: {count}</span>
            ))
          )}
        </div>
      </div>

      <div className={`${CT.panel} p-5`} data-testid="intake-analytics-recent-activity">
        <p className={CT.cardTitle}>Recent Activity</p>
        {data.recent_activity.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">No scans or submissions yet.</p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {data.recent_activity.slice(0, 10).map((e, idx) => (
              <div key={idx} className="flex items-center justify-between text-xs text-slate-400" data-testid={`intake-analytics-activity-${idx}`}>
                <span>{e.event_type === "scan" ? "QR Scan" : "Submission"} · {e.source}</span>
                <span>{new Date(e.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
